import { useCallback, useEffect, useState } from 'react';
import type { CropArea } from './StanCrop.types';

// Helper function to create cropped image
const createImage = (url: string): Promise<HTMLImageElement> =>
    new Promise((resolve, reject) => {
        const image = new Image();
        image.addEventListener('load', () => resolve(image));
        image.addEventListener('error', error => reject(error));
        image.setAttribute('crossOrigin', 'anonymous');
        image.src = url;
    });

const blobToFile = (blob: Blob, filename: string) => {
    // Extract the file extension from the input filename
    const extension = filename.split('.').pop();

    // Automatically assign the MIME type based on file extension
    const mimeTypes = {
        jpg: 'image/jpeg',
        jpeg: 'image/jpeg',
        png: 'image/png',
    };

    const mimeType = mimeTypes[extension as keyof typeof mimeTypes] || 'application/octet-stream'; // Default MIME type if unknown

    // Create a File with the determined filename and MIME type
    const file = new File([blob], filename, { type: mimeType });
    return file;
};

export const getCroppedImg = async (
    imageSrc: string,
    pixelCrop: CropArea,
    _fileName: string = 'cropped-image.jpg',
    _borderRadius?: number | 'circle'
): Promise<{ blob: Blob; dataUrl: string }> => {
    const image = await createImage(imageSrc);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) {
        throw new Error('Could not get canvas context');
    }

    canvas.width = pixelCrop.width;
    canvas.height = pixelCrop.height;

    // Draw the cropped image
    ctx.drawImage(
        image,
        pixelCrop.x,
        pixelCrop.y,
        pixelCrop.width,
        pixelCrop.height,
        0,
        0,
        pixelCrop.width,
        pixelCrop.height
    );

    // Note: borderRadius is only used for preview UI, not applied to the actual output
    // The output is always a square PNG

    return new Promise((resolve, reject) => {
        const format = 'image/png';
        canvas.toBlob(blob => {
            if (!blob) {
                reject(new Error('Canvas is empty'));
                return;
            }
            const dataUrl = canvas.toDataURL(format);
            resolve({ blob, dataUrl });
        }, format);
    });
};

export const calculateInitialZoom = (imageWidth: number, imageHeight: number, cropAspectRatio: number): number => {
    const imageAspect = imageWidth / imageHeight;

    // Calculate zoom needed to fill the crop area completely
    let calculatedZoom = 1;
    if (imageAspect > cropAspectRatio) {
        // Image is wider than crop area - fit by height (zoom in)
        calculatedZoom = imageAspect / cropAspectRatio;
    } else {
        // Image is taller than crop area - fit by width (zoom in)
        calculatedZoom = cropAspectRatio / imageAspect;
    }

    // Ensure minimum zoom is at least 1
    return Math.max(1, calculatedZoom);
};

export const useCropImage = (
    imageFile: File | null,
    isOpen: boolean,
    calculatedAspectRatio: number,
    borderRadius: number | 'circle',
    onConfirm: (data: { croppedBlob: Blob; croppedDataUrl: string; originalFile: File; croppedFile: File }) => void
) => {
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState<CropArea | null>(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [imageUrl, setImageUrl] = useState<string>('');
    const [showCropper, setShowCropper] = useState(false);

    // Create and cleanup image URL when file changes
    useEffect(() => {
        if (imageFile && isOpen) {
            const url = URL.createObjectURL(imageFile);
            setImageUrl(url);
            // Reset crop state
            setCrop({ x: 0, y: 0 });
            setCroppedAreaPixels(null);
            setShowCropper(false);

            // Calculate initial zoom to fill the canvas
            const img = new Image();
            img.onload = () => {
                const calculatedZoom = calculateInitialZoom(img.width, img.height, calculatedAspectRatio);
                setZoom(calculatedZoom);
            };
            img.src = url;

            // Delay showing cropper to ensure modal is fully rendered
            const timer = setTimeout(() => {
                setShowCropper(true);
            }, 100);

            return () => {
                clearTimeout(timer);
                URL.revokeObjectURL(url);
            };
        } else {
            setImageUrl('');
            setShowCropper(false);
        }
        return undefined;
    }, [imageFile, isOpen, calculatedAspectRatio]);

    const onCropComplete = useCallback((_: CropArea, croppedPixels: CropArea) => {
        setCroppedAreaPixels(croppedPixels);
    }, []);

    const onCropChange = useCallback((c: { x: number; y: number }) => {
        setCrop(c);
    }, []);

    const onZoomChange = useCallback((z: number) => {
        setZoom(z);
    }, []);

    const handleConfirm = useCallback(async () => {
        if (!croppedAreaPixels || !imageFile || !imageUrl) return;
        setIsProcessing(true);
        try {
            const { blob, dataUrl } = await getCroppedImg(imageUrl, croppedAreaPixels, imageFile.name, borderRadius);
            const croppedFile = blobToFile(blob, imageFile.name);
            onConfirm({ croppedFile, croppedBlob: blob, croppedDataUrl: dataUrl, originalFile: imageFile });
        } catch {
            // Error cropping image - could show user notification here
        } finally {
            setIsProcessing(false);
        }
    }, [croppedAreaPixels, imageFile, imageUrl, borderRadius, onConfirm]);

    return {
        crop,
        zoom,
        croppedAreaPixels,
        isProcessing,
        imageUrl,
        showCropper,
        onCropComplete,
        onCropChange,
        onZoomChange,
        handleConfirm,
    };
};
